<div data-spartan-page class="p-6 md:p-8 space-y-6"
     style="background: rgb(var(--ds-bg, 250 250 249)); min-height: 100%;">

  <!-- Back / breadcrumb -->
  <div class="flex items-center gap-2">
    <button hlmBtn variant="ghost" size="sm" (click)="goBack()">
      <ng-icon hlm name="lucideArrowLeft" class="mr-1.5 size-4" />
      Pre-Alerts
    </button>
    @if (preAlert) {
      <span class="text-xs text-muted-foreground">/</span>
      <span class="text-xs font-mono text-foreground">{{ preAlert.ladingBill }}</span>
    }
  </div>

  <!-- Loading -->
  @if (isLoading) {
    <div class="space-y-4">
      <app-skeleton [rows]="2" rowHeight="h-8" />
      <app-skeleton [rows]="6" />
      <app-skeleton [rows]="6" />
    </div>

  <!-- Error -->
  } @else if (error && !preAlert) {
    <app-inline-alert tone="danger" [message]="error" />

  <!-- Not found -->
  } @else if (!isLoading && !preAlert && !error) {
    <app-empty-state
      icon="lucideFileText"
      heading="Pre-Alert Not Found"
      description="The requested pre-alert could not be found."
    >
      <button hlmBtn variant="outline" size="sm" (click)="goBack()">
        <ng-icon hlm name="lucideArrowLeft" class="mr-1.5 size-4" />
        Back to List
      </button>
    </app-empty-state>

  } @else if (preAlert) {

    <!-- Page header -->
    <app-page-header
      eyebrow="Shopee / Pre-Alerts"
      [title]="preAlert.ladingBill"
      subtitle="Pre-alert vessel shipment details"
    >
      <div actions class="flex flex-wrap items-center gap-2">
        <app-status-badge
          [variant]="getPreAlertStatusVariant(preAlert.status)"
          [label]="preAlert.status"
        />

        @if (!(isShopeeChecker$ | async)) {
          <!-- Export dropdown (Bootstrap-native — logic preserved) -->
          <div class="btn-group">
            <button type="button" class="btn btn-sm btn-outline-secondary dropdown-toggle"
                    data-bs-toggle="dropdown" aria-expanded="false">
              <ng-icon hlm name="lucideDownload" class="mr-1.5 size-3.5 inline-block" />
              Export Excel
            </button>
            <ul class="dropdown-menu dropdown-menu-end">
              <li>
                <button class="dropdown-item text-sm"
                        (click)="exportLMWarehouse()"
                        [disabled]="isExportingLMWarehouse">
                  @if (isExportingLMWarehouse) {
                    <span class="spinner-border spinner-border-sm me-2"></span>
                    Exporting…
                  } @else {
                    <ng-icon hlm name="lucideWarehouse" class="me-2 size-4 inline-block" />
                    Export LM Warehouse
                  }
                </button>
              </li>
              <li>
                <button class="dropdown-item text-sm"
                        (click)="exportTruckLoading()"
                        [disabled]="isExportingTruckLoading">
                  @if (isExportingTruckLoading) {
                    <span class="spinner-border spinner-border-sm me-2"></span>
                    Exporting…
                  } @else {
                    <ng-icon hlm name="lucideTruck" class="me-2 size-4 inline-block" />
                    Export Truck Loading
                  }
                </button>
              </li>
            </ul>
          </div>

          <button hlmBtn variant="outline" size="sm"
                  (click)="openEditModal()"
                  [disabled]="!preAlert || isEditing">
            <ng-icon hlm name="lucidePencil" class="mr-1.5 size-3.5" />
            {{ isEditing ? 'Saving…' : 'Edit' }}
          </button>

          <button hlmBtn variant="destructive" size="sm"
                  (click)="openDeleteConfirmation()"
                  [disabled]="!preAlert || isDeleting">
            <ng-icon hlm name="lucideTrash2" class="mr-1.5 size-3.5" />
            {{ isDeleting ? 'Deleting…' : 'Delete' }}
          </button>
        }

        <button hlmBtn variant="ghost" size="sm"
                (click)="refresh()" [disabled]="isLoading"
                title="Refresh">
          <ng-icon hlm name="lucideRefreshCw"
                   class="size-3.5"
                   [class.animate-spin]="isLoading" />
        </button>
      </div>
    </app-page-header>

    <!-- Error inline (load errors that show alongside data) -->
    @if (error) {
      <app-inline-alert tone="danger" [message]="error" />
    }

    <!-- ── Two-column info grid ── -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">

      <!-- Basic Information -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Basic Information</h2>
        </div>
        <div class="p-4">
          <dl class="divide-y divide-border">
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Lading Bill</dt>
              <dd class="text-right font-mono text-[12.5px] font-semibold text-foreground">{{ preAlert.ladingBill }}</dd>
            </div>
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Transport Company</dt>
              <dd class="text-right text-foreground">{{ preAlert.transportCompany }}</dd>
            </div>
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Vessel Number</dt>
              <dd class="text-right font-mono text-[12px] text-foreground">{{ preAlert.vesselNumber }}</dd>
            </div>
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Carton Count</dt>
              <dd class="text-right tabular-nums font-medium text-foreground">{{ preAlert.cartonCount }}</dd>
            </div>
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Pieces</dt>
              <dd class="text-right tabular-nums text-foreground">{{ preAlert.pieces }}</dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Route & Schedule -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Route &amp; Schedule</h2>
        </div>
        <div class="p-4">
          <dl class="divide-y divide-border">
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Departure</dt>
              <dd class="text-right text-foreground">{{ preAlert.departureLocation }}</dd>
            </div>
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">Arrival</dt>
              <dd class="text-right text-foreground">{{ preAlert.arrivalLocation }}</dd>
            </div>
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">ETD</dt>
              <dd class="text-right tabular-nums text-xs text-foreground">{{ formatDate(preAlert.etd) }}</dd>
            </div>
            <div class="py-2 flex justify-between gap-4 text-sm">
              <dt class="font-medium shrink-0 text-muted-foreground">ETA</dt>
              <dd class="text-right">
                <div class="tabular-nums text-xs" [class]="getETAStatusClass(preAlert.eta)">
                  {{ formatDate(preAlert.eta) }}
                </div>
                <app-status-badge
                  class="mt-0.5 block"
                  [variant]="getEtaVariant(preAlert.eta)"
                  [label]="getEtaLabel(preAlert.eta)"
                />
              </dd>
            </div>
          </dl>
        </div>
      </div>

      <!-- Weight Information -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">Weight Information</h2>
        </div>
        <div class="p-4">
          <dl class="divide-y divide-border">
            @for (row of [
              { label: 'Weight',             value: formatWeight(preAlert.weight) },
              { label: 'Chargeable Weight',  value: formatWeight(preAlert.chargeableWeight) },
              { label: 'Volumetric Weight',  value: formatWeight(preAlert.volumetricWeight) },
              { label: 'Pieces',             value: preAlert.pieces.toString() }
            ]; track row.label) {
              <div class="py-2 flex justify-between gap-4 text-sm">
                <dt class="font-medium shrink-0 text-muted-foreground">{{ row.label }}</dt>
                <dd class="text-right tabular-nums font-mono text-[12.5px] text-foreground">{{ row.value }}</dd>
              </div>
            }
          </dl>
        </div>
      </div>

      <!-- System Information -->
      <div hlmCard>
        <div class="px-4 py-3 border-b border-border">
          <h2 class="text-sm font-semibold text-foreground">System Information</h2>
        </div>
        <div class="p-4">
          <dl class="divide-y divide-border">
            @if (preAlert.createdAtUtc) {
              <div class="py-2 flex justify-between gap-4 text-sm">
                <dt class="font-medium shrink-0 text-muted-foreground">Created</dt>
                <dd class="text-right tabular-nums text-xs text-foreground">{{ formatDate(preAlert.createdAtUtc) }}</dd>
              </div>
            }
            @if (preAlert.lastUpdatedAtUtc) {
              <div class="py-2 flex justify-between gap-4 text-sm">
                <dt class="font-medium shrink-0 text-muted-foreground">Last Updated</dt>
                <dd class="text-right tabular-nums text-xs text-foreground">{{ formatDate(preAlert.lastUpdatedAtUtc) }}</dd>
              </div>
            }
          </dl>
        </div>
      </div>

    </div><!-- /2-col grid -->

    <!-- ── Cartons table ── -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border flex items-center justify-between">
        <h2 class="text-sm font-semibold text-foreground">
          Cartons
          <span class="ml-1.5 tabular-nums font-normal text-xs text-muted-foreground">
            ({{ cartons.length }}) · {{ getTotalParcelCount() }} parcels total
          </span>
        </h2>
      </div>

      @if (isLoading) {
        <div class="p-4"><app-skeleton [rows]="3" /></div>
      } @else if (cartons.length === 0) {
        <app-empty-state
          icon="lucidePackage"
          heading="No cartons"
          description="No cartons available for this pre-alert."
        />
      } @else {
        <div class="overflow-x-auto">
          <table class="w-full text-sm" aria-label="Cartons">
            <thead>
              <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                <th class="text-left px-4 py-2.5 font-medium w-10">#</th>
                <th class="text-left px-4 py-2.5 font-medium">Carton Number</th>
                <th class="text-left px-4 py-2.5 font-medium">Unique ID</th>
                <th class="text-right px-4 py-2.5 font-medium">Parcels</th>
                <th class="text-right px-4 py-2.5 font-medium">Weight</th>
                <th class="text-right px-4 py-2.5 font-medium">Volume</th>
                <th class="text-left px-4 py-2.5 font-medium">Status</th>
                <th class="text-right px-4 py-2.5 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              @for (carton of cartons; track carton.uniqueId; let i = $index) {
                <tr class="h-12 border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
                  <td class="px-4 text-xs tabular-nums text-muted-foreground">{{ i + 1 }}</td>
                  <td class="px-4">
                    <code class="font-mono text-[12px] px-1.5 py-0.5 rounded text-xs bg-surface-raised">
                      {{ carton.cartonNo }}
                    </code>
                  </td>
                  <td class="px-4 text-xs font-mono text-muted-foreground">
                    {{ carton.uniqueId }}
                  </td>
                  <td class="px-4 text-right tabular-nums">{{ carton.parcelCount }}</td>
                  <td class="px-4 text-right tabular-nums text-xs">
                    @if (carton.weight) { {{ formatWeight(carton.weight) }} } @else { — }
                  </td>
                  <td class="px-4 text-right tabular-nums text-xs">
                    @if (carton.volume) { {{ carton.volume }} cm³ } @else { — }
                  </td>
                  <td class="px-4">
                    @if (carton.isScanned) {
                      <app-status-badge variant="success" label="Scanned" />
                    } @else {
                      <app-status-badge variant="warning" label="Not Scanned" />
                    }
                  </td>
                  <td class="px-4 text-right">
                    <div class="flex items-center justify-end gap-1">
                      @if (carton.parcelCount > 0) {
                        <button class="btn btn-sm btn-outline-info"
                                type="button"
                                data-bs-toggle="collapse"
                                [attr.data-bs-target]="'#parcels-' + i"
                                [attr.aria-expanded]="false"
                                [attr.aria-controls]="'parcels-' + i"
                                title="View parcels">
                          <ng-icon hlm name="lucideEye" class="size-3.5" />
                        </button>
                      }
                      @if (!(isShopeeChecker$ | async)) {
                        <button hlmBtn variant="ghost" size="sm"
                                class="h-7 w-7 p-0 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
                                type="button"
                                (click)="openCartonDeleteConfirmation(carton)"
                                [disabled]="isDeletingCarton"
                                title="Delete carton">
                          <ng-icon hlm name="lucideTrash2" class="size-3.5" />
                        </button>
                      }
                    </div>
                  </td>
                </tr>

                <!-- Expandable parcels row (Bootstrap collapse) -->
                @if (carton.parcels.length > 0) {
                  <tr class="collapse" [attr.id]="'parcels-' + i">
                    <td colspan="8" class="p-0">
                      <div class="border-l-2 border-ds-accent ml-4 my-2 pl-4 pr-4 py-3 bg-surface-raised">
                        <div class="text-xs font-medium mb-2 text-muted-foreground">
                          Parcels in {{ carton.cartonNo }}
                        </div>
                        <div class="overflow-x-auto">
                          <table class="w-full text-[13px]">
                            <thead>
                              <tr class="text-[10.5px] font-medium uppercase tracking-wider text-muted-foreground">
                                <th class="text-left px-3 py-1.5 font-medium">Tracking #</th>
                                <th class="text-right px-3 py-1.5 font-medium">Weight</th>
                                <th class="text-left px-3 py-1.5 font-medium">Dimensions</th>
                                <th class="text-right px-3 py-1.5 font-medium">Value</th>
                                <th class="text-left px-3 py-1.5 font-medium">Recipient</th>
                                <th class="text-left px-3 py-1.5 font-medium">Status</th>
                              </tr>
                            </thead>
                            <tbody>
                              @for (parcel of carton.parcels; track parcel.parcel_tracking_number) {
                                <tr class="border-t border-border">
                                  <td class="px-3 py-1.5 font-mono text-[12px] text-ds-accent">
                                    {{ parcel.parcel_tracking_number }}
                                  </td>
                                  <td class="px-3 py-1.5 text-right tabular-nums">
                                    @if (parcel.weight) { {{ formatWeight(parcel.weight) }} } @else { — }
                                  </td>
                                  <td class="px-3 py-1.5 text-xs text-muted-foreground">
                                    {{ parcel.dimensions || '—' }}
                                  </td>
                                  <td class="px-3 py-1.5 text-right tabular-nums text-xs">
                                    @if (parcel.value) { ${{ parcel.value.toFixed(2) }} } @else { — }
                                  </td>
                                  <td class="px-3 py-1.5 text-xs">{{ parcel.recipient || '—' }}</td>
                                  <td class="px-3 py-1.5">
                                    @if (parcel.status) {
                                      <app-status-badge variant="neutral" [label]="parcel.status" />
                                    } @else {
                                      <span class="text-muted-foreground">—</span>
                                    }
                                  </td>
                                </tr>
                              }
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>
      }
    </div>

    <!-- ── Export History ── -->
    <div hlmCard>
      <div class="px-4 py-3 border-b border-border">
        <h2 class="text-sm font-semibold text-foreground">
          Export History
          <span class="ml-1.5 tabular-nums font-normal text-xs text-muted-foreground">
            ({{ exportHistory.length }})
          </span>
        </h2>
      </div>

      @if (isLoadingExportHistory) {
        <div class="p-4"><app-skeleton [rows]="2" /></div>
      } @else if (exportHistory.length === 0) {
        <div class="px-4 py-8 text-center text-sm text-muted-foreground">
          No export history available.
        </div>
      } @else {
        <div class="overflow-x-auto">
          <table class="w-full text-sm" aria-label="Export history">
            <thead>
              <tr class="text-[11px] font-medium uppercase tracking-wider text-muted-foreground border-b border-border-strong">
                <th class="text-left px-4 py-2.5 font-medium">Export Type</th>
                <th class="text-left px-4 py-2.5 font-medium">Status</th>
                <th class="text-left px-4 py-2.5 font-medium">Exported At</th>
                <th class="text-left px-4 py-2.5 font-medium">Exported By</th>
                <th class="text-left px-4 py-2.5 font-medium">Filename</th>
              </tr>
            </thead>
            <tbody>
              @for (item of exportHistory; track item.id) {
                <tr class="h-12 border-b border-border last:border-0 even:bg-surface-raised/50 hover:bg-surface-raised transition-colors">
                  <td class="px-4 text-sm">{{ item.exportType }}</td>
                  <td class="px-4">
                    <app-status-badge
                      [variant]="getExportStatusVariant(item.status)"
                      [label]="item.status"
                    />
                  </td>
                  <td class="px-4 text-xs tabular-nums text-muted-foreground">
                    {{ formatDate(item.exportedAtUtc) }}
                  </td>
                  <td class="px-4 text-sm">{{ item.exportedBy }}</td>
                  <td class="px-4">
                    @if (item.filename) {
                      <code class="font-mono text-[11px] px-1.5 py-0.5 rounded bg-surface-raised">
                        {{ item.filename }}
                      </code>
                    } @else {
                      <span class="text-muted-foreground">—</span>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      }
    </div>

  }<!-- /if preAlert -->

</div>


<!-- ═══════════════════════════════════════════════════════
     MODALS — preserved intact, only minor template syntax modernized
     ═══════════════════════════════════════════════════════ -->

<!-- Edit Pre-Alert Modal -->
@if (showEditModal) {
  <div class="modal fade show" style="display:block" tabindex="-1"
       aria-labelledby="editPreAlertModalLabel"
       (click)="closeEditModal()">
    <div class="modal-dialog modal-lg" (click)="$event.stopPropagation()">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editPreAlertModalLabel">
            <i class="bi bi-pencil-square me-2"></i>
            Edit Pre-Alert
          </h5>
          <button type="button" class="btn-close" (click)="closeEditModal()"></button>
        </div>
        @if (editForm) {
          <form [formGroup]="editForm" (ngSubmit)="savePreAlert()">
            <div class="modal-body">
              @if (error) {
                <div class="alert alert-danger">
                  <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
                </div>
              }
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Lading Bill</label>
                  <input type="text" class="form-control bg-light" formControlName="ladingBill" readonly>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Vessel <span class="text-danger">*</span></label>
                  <input type="text" class="form-control"
                         [class.is-invalid]="hasFieldError('vessel')"
                         formControlName="vessel" placeholder="Enter vessel name">
                  <div class="invalid-feedback">{{ getFieldError('vessel') }}</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Voyage <span class="text-danger">*</span></label>
                  <input type="text" class="form-control"
                         [class.is-invalid]="hasFieldError('voyage')"
                         formControlName="voyage" placeholder="Enter voyage number">
                  <div class="invalid-feedback">{{ getFieldError('voyage') }}</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">ETA <span class="text-danger">*</span></label>
                  <input type="date" class="form-control"
                         [class.is-invalid]="hasFieldError('eta')"
                         formControlName="eta">
                  <div class="invalid-feedback">{{ getFieldError('eta') }}</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">ETD <span class="text-danger">*</span></label>
                  <input type="date" class="form-control"
                         [class.is-invalid]="hasFieldError('etd')"
                         formControlName="etd">
                  <div class="invalid-feedback">{{ getFieldError('etd') }}</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Port of Loading <span class="text-danger">*</span></label>
                  <input type="text" class="form-control"
                         [class.is-invalid]="hasFieldError('portOfLoading')"
                         formControlName="portOfLoading" placeholder="Enter port of loading">
                  <div class="invalid-feedback">{{ getFieldError('portOfLoading') }}</div>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Port of Discharge <span class="text-danger">*</span></label>
                  <input type="text" class="form-control"
                         [class.is-invalid]="hasFieldError('portOfDischarge')"
                         formControlName="portOfDischarge" placeholder="Enter port of discharge">
                  <div class="invalid-feedback">{{ getFieldError('portOfDischarge') }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Total Cartons <span class="text-danger">*</span></label>
                  <input type="number" class="form-control"
                         [class.is-invalid]="hasFieldError('totalCartons')"
                         formControlName="totalCartons" min="1" placeholder="0">
                  <div class="invalid-feedback">{{ getFieldError('totalCartons') }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Total Weight (kg) <span class="text-danger">*</span></label>
                  <input type="number" class="form-control"
                         [class.is-invalid]="hasFieldError('totalWeight')"
                         formControlName="totalWeight" min="0.1" step="0.1" placeholder="0.0">
                  <div class="invalid-feedback">{{ getFieldError('totalWeight') }}</div>
                </div>
                <div class="col-md-4">
                  <label class="form-label">Total Volume (cbm) <span class="text-danger">*</span></label>
                  <input type="number" class="form-control"
                         [class.is-invalid]="hasFieldError('totalVolume')"
                         formControlName="totalVolume" min="0.01" step="0.01" placeholder="0.00">
                  <div class="invalid-feedback">{{ getFieldError('totalVolume') }}</div>
                </div>
                <div class="col-12">
                  <label class="form-label">Remarks</label>
                  <textarea class="form-control" formControlName="remarks" rows="3"
                            placeholder="Enter additional remarks (optional)"></textarea>
                </div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" (click)="closeEditModal()">Cancel</button>
              <button type="submit" class="btn btn-primary"
                      [disabled]="editForm.invalid || isEditing">
                @if (isEditing) {
                  <span class="spinner-border spinner-border-sm me-1"></span>
                  Saving…
                } @else {
                  Save Changes
                }
              </button>
            </div>
          </form>
        }
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
}

<!-- Carton Delete Confirmation Modal -->
@if (showCartonDeleteConfirmation && cartonToDelete) {
  <div class="modal fade show" style="display:block" tabindex="-1"
       aria-labelledby="deleteCartonModalLabel"
       (click)="closeCartonDeleteConfirmation()">
    <div class="modal-dialog" (click)="$event.stopPropagation()">
      <div class="modal-content">
        <div class="modal-header bg-danger text-white">
          <h5 class="modal-title" id="deleteCartonModalLabel">
            <i class="bi bi-exclamation-triangle me-2"></i>
            Delete Carton
          </h5>
          <button type="button" class="btn-close btn-close-white"
                  (click)="closeCartonDeleteConfirmation()"></button>
        </div>
        <div class="modal-body">
          <div class="alert alert-warning">
            <i class="bi bi-info-circle me-2"></i>
            <strong>Warning:</strong> This action cannot be undone.
          </div>
          <p>Are you sure you want to delete carton <strong>{{ cartonToDelete.cartonNo }}</strong>?</p>
          @if (error) {
            <div class="alert alert-danger mt-3">
              <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
            </div>
          }
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary"
                  (click)="closeCartonDeleteConfirmation()">Cancel</button>
          <button type="button" class="btn btn-danger"
                  (click)="deleteCarton()"
                  [disabled]="isDeletingCarton">
            @if (isDeletingCarton) {
              <span class="spinner-border spinner-border-sm me-1"></span>
              Deleting…
            } @else {
              Delete Carton
            }
          </button>
        </div>
      </div>
    </div>
  </div>
  <div class="modal-backdrop fade show"></div>
}

<!-- Delete Confirmation component modal -->
@if (preAlert) {
  <app-delete-confirmation
    [preAlert]="preAlert"
    [isVisible]="showDeleteConfirmation"
    (confirmed)="onDeleteConfirmed()"
    (cancelled)="onDeleteCancelled()">
  </app-delete-confirmation>
}
